import React, { forwardRef } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import {
    Bolt as BoltIcon,
    Psychology as PsychologyIcon,
    Speed as SpeedIcon,
    LocalFireDepartment as FireIcon,
    EmojiEvents as TrophyIcon,
    MilitaryTech as MedalIcon,
    MenuBook as BookIcon,
    Translate as TranslateIcon,
    Timer as TimerIcon,
} from '@mui/icons-material';

const RARITY_COLORS = {
    common: '#9E9E9E',
    uncommon: '#4CAF50',
    rare: '#2196F3',
    epic: '#9C27B0',
    legendary: '#FF9800',
};

const CARD_GRADIENTS = {
    session: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #01579b 100%)',
    badge: 'linear-gradient(135deg, #4a148c 0%, #6a1b9a 50%, #7b1fa2 100%)',
    streak: 'linear-gradient(135deg, #bf360c 0%, #d84315 50%, #e64a19 100%)',
    leaderboard: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 50%, #388e3c 100%)',
    challenge: 'linear-gradient(135deg, #311b92 0%, #4527a0 50%, #512da8 100%)',
};

const ASPECT_STYLES = {
    '1:1': { width: 400, minHeight: 400 },
    '4:5': { width: 400, minHeight: 500 },
    '9:16': { width: 360, minHeight: 640 },
    '16:9': { width: 640, minHeight: 360 },
};

const MetricRow = ({ icon, label, value, color = 'white' }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {React.createElement(icon, { sx: { fontSize: 18, opacity: 0.8, color } })}
        <Typography variant="body2" sx={{ color, opacity: 0.8, fontSize: '0.8rem' }}>
            {label}:
        </Typography>
        <Typography variant="body2" sx={{ color, fontWeight: 700, fontSize: '0.9rem' }}>
            {value}
        </Typography>
    </Box>
);

const FlexCardVisual = forwardRef(({ card, qrCodeDataUrl, showQr = true }, ref) => {
    if (!card) return null;

    const gradient = CARD_GRADIENTS[card.cardType] || CARD_GRADIENTS.session;
    const aspectStyle = ASPECT_STYLES[card.aspectRatio] || ASPECT_STYLES['1:1'];
    const metrics = card.metrics || {};
    const cardTypeLabel = card.cardType ? card.cardType.charAt(0).toUpperCase() + card.cardType.slice(1) : '';

    return (
        <Box
            ref={ref}
            sx={{
                ...aspectStyle,
                background: gradient,
                borderRadius: 3,
                padding: 3,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                mx: 'auto',
                fontFamily: '"Inter", "Roboto", sans-serif',
            }}
        >
            {/* Decorative glow */}
            <Box
                sx={{
                    position: 'absolute',
                    top: -60,
                    right: -60,
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.05)',
                    pointerEvents: 'none',
                }}
            />

            {/* Header */}
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <BoltIcon sx={{ color: '#FFD700', fontSize: 24 }} />
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'rgba(255,255,255,0.7)',
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                        }}
                    >
                        ReaderFLEX
                    </Typography>
                </Box>

                {/* Card type badge */}
                <Chip
                    label={cardTypeLabel}
                    size="small"
                    sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        color: 'white',
                        textTransform: 'capitalize',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        mb: 1.5,
                    }}
                />

                {/* Headline */}
                <Typography
                    variant="h4"
                    sx={{
                        color: 'white',
                        fontWeight: 800,
                        lineHeight: 1.1,
                        mb: 0.5,
                        fontSize: card.cardType === 'streak' ? '2.5rem' : '2rem',
                    }}
                >
                    {card.headline}
                </Typography>

                {/* Subheadline (display name) */}
                {card.subheadline && (
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'rgba(255,255,255,0.7)',
                            fontWeight: 500,
                            mb: 2,
                        }}
                    >
                        {card.subheadline}
                    </Typography>
                )}
            </Box>

            {/* Metrics section */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 2 }}>
                {card.cardType === 'session' && (
                    <>
                        {metrics.wpm != null && (
                            <MetricRow icon={SpeedIcon} label="WPM" value={metrics.wpm} />
                        )}
                        {metrics.comprehension != null && (
                            <MetricRow icon={PsychologyIcon} label="Comprehension" value={`${metrics.comprehension}%`} />
                        )}
                        {metrics.effectiveWpm != null && (
                            <MetricRow icon={BoltIcon} label="Effective WPM" value={metrics.effectiveWpm} color="#FFD700" />
                        )}
                        {metrics.wordsRead != null && (
                            <MetricRow icon={BookIcon} label="Words Read" value={metrics.wordsRead.toLocaleString()} />
                        )}
                        {metrics.readingTime != null && (
                            <MetricRow icon={TimerIcon} label="Reading Time" value={`${Math.round(metrics.readingTime)} min`} />
                        )}
                        {metrics.language && metrics.language !== 'en' && (
                            <MetricRow icon={TranslateIcon} label="Language" value={metrics.language.toUpperCase()} />
                        )}
                    </>
                )}

                {card.cardType === 'streak' && (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <FireIcon sx={{ fontSize: 64, color: '#FFD700', mb: 1 }} />
                        <Typography variant="h2" sx={{ color: 'white', fontWeight: 900 }}>
                            {metrics.streak}
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            Day Streak
                        </Typography>
                    </Box>
                )}

                {card.cardType === 'badge' && (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <MedalIcon
                            sx={{
                                fontSize: 64,
                                color: RARITY_COLORS[metrics.badgeRarity] || '#FFD700',
                                mb: 1,
                            }}
                        />
                        <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                            {metrics.badgeName}
                        </Typography>
                        {metrics.badgeCategory && (
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'capitalize' }}>
                                {metrics.badgeCategory}
                            </Typography>
                        )}
                        {metrics.badgeRarity && (
                            <Chip
                                label={metrics.badgeRarity}
                                size="small"
                                sx={{
                                    mt: 1,
                                    bgcolor: `${RARITY_COLORS[metrics.badgeRarity]}40`,
                                    color: RARITY_COLORS[metrics.badgeRarity],
                                    textTransform: 'capitalize',
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                }}
                            />
                        )}
                    </Box>
                )}

                {card.cardType === 'leaderboard' && (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <TrophyIcon sx={{ fontSize: 64, color: '#FFD700', mb: 1 }} />
                        <Typography variant="h3" sx={{ color: 'white', fontWeight: 900 }}>
                            #{metrics.leaderboardRank}
                        </Typography>
                        {metrics.leaderboardCategory && (
                            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', textTransform: 'capitalize' }}>
                                {metrics.leaderboardCategory}
                            </Typography>
                        )}
                    </Box>
                )}

                {card.cardType === 'challenge' && (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                            {metrics.challengeResult}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Footer with QR code */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <Box>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'rgba(255,255,255,0.5)',
                            fontSize: '0.65rem',
                            display: 'block',
                        }}
                    >
                        Take the ReaderFLEX test
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'rgba(255,255,255,0.4)',
                            fontSize: '0.6rem',
                        }}
                    >
                        readerflex.com
                    </Typography>
                </Box>
                {showQr && qrCodeDataUrl && (
                    <Box
                        component="img"
                        src={qrCodeDataUrl}
                        alt="QR Code"
                        sx={{
                            width: 70,
                            height: 70,
                            borderRadius: 1,
                            bgcolor: 'white',
                            padding: '4px',
                        }}
                    />
                )}
            </Box>
        </Box>
    );
});

FlexCardVisual.displayName = 'FlexCardVisual';

export default FlexCardVisual;
